import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Flame, Target } from 'lucide-react';

interface StatsData {
  totalExams: number;
  streakDays: number;
  avgScore: number;
  studiedToday: boolean;
}

interface StatsCardsProps {
  stats: StatsData;
}

export const StatsCards: React.FC<StatsCardsProps> = ({ stats }) => {
  const { t } = useTranslation();

  const streakActive = stats.streakDays > 0 && stats.studiedToday;
  const dayLabel = stats.streakDays === 1 ? t('common.day') : t('common.days');

  const cards = [
    {
      icon: BookOpen,
      label: t('examHistory.totalExams'),
      value: stats.totalExams.toString(),
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      dimmed: false,
    },
    {
      icon: Flame,
      label: t('profile.stats.dayStreak'),
      value: `${stats.streakDays} ${dayLabel}`,
      color: streakActive ? 'text-secondary' : 'text-gray-300',
      bgColor: streakActive ? 'bg-secondary/10' : 'bg-gray-100',
      dimmed: !streakActive,
    },
    {
      icon: Target,
      label: t('examHistory.avgScore'),
      value: `${stats.avgScore}%`,
      color: 'text-success',
      bgColor: 'bg-success/10',
      dimmed: false,
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
          <Target className="w-4 h-4 text-green-600" />
        </div>
        <h3 className="font-semibold text-gray-900">{t('dashboard.yourStats')}</h3>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {cards.map(({ icon: Icon, label, value, color, bgColor, dimmed }) => (
          <div
            key={label}
            className={`flex items-center gap-3 ${dimmed ? 'opacity-40' : ''}`}
          >
            <div className={`w-10 h-10 rounded-lg ${bgColor} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900 leading-tight">{value}</p>
              <p className="text-sm text-gray-500">{label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
